import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Trash, Warning } from "@phosphor-icons/react";

/**
 * Reusable delete button with confirmation modal.
 * Props:
 *  - url: DELETE endpoint (e.g. "/dancers/abc")
 *  - label: what is being deleted (e.g. "la danseuse Luna")
 *  - hint: optional warning text (e.g. "Cela supprimera aussi ses bookings et règlements")
 *  - onDone: callback after success
 *  - size: "sm" | "default"
 *  - variant: "icon" (just trash icon) | "full" (icon + label)
 *  - testId: data-testid
 */
export default function DeleteButton({
  url,
  label,
  hint,
  onDone,
  size = "sm",
  variant = "icon",
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await api.delete(url);
      toast.success(`${label} supprimé${label.endsWith("s") ? "s" : ""}`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de suppression");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant="outline"
        onClick={() => setOpen(true)}
        className="h-8 border-[#27272a] hover:bg-[#3f1d1d] hover:border-[#f87171] text-[#f87171]"
        data-testid={testId}
      >
        <Trash size={14} />
        {variant === "full" && <span className="ml-2">Supprimer</span>}
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-[#121217] border-[#27272a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl flex items-center gap-2">
              <Warning size={20} color="#f87171" weight="fill" />
              Supprimer {label} ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#a1a1aa]">
              Cette action est <b className="text-[#f87171]">irréversible</b>.
              {hint && <div className="mt-2">{hint}</div>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-[#27272a] text-white hover:bg-[#1a1a21]"
              data-testid={`${testId}-cancel`}
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              className="bg-[#f87171] hover:bg-[#ef4444] text-white"
              data-testid={`${testId}-confirm`}
              onClick={run}
            >
              {busy ? "Suppression..." : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Wipe-section button: clears a whole collection after double confirmation.
 *  - section: "dancers" | "payment_requests" | "staff" | "daily_cash" | "reports" | etc
 *  - label: "les danseuses et leurs activités"
 */
export function WipeSectionButton({ section, label, onDone, testId }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  const close = () => {
    setOpen(false);
    setStep(1);
  };

  const wipe = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/wipe/${section}`);
      toast.success(`Section vidée — ${Object.entries(r.data.deleted).map(([k, v]) => `${k}:${v}`).join(", ")}`);
      close();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="h-9 border-[#7f1d1d] bg-[#1a0b0b] text-[#f87171] hover:bg-[#3f1d1d]"
        data-testid={testId}
      >
        <Trash size={14} className="mr-2" />
        Tout vider
      </Button>

      <AlertDialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
        <AlertDialogContent className="bg-[#121217] border-[#27272a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl flex items-center gap-2">
              <Warning size={22} color="#f87171" weight="fill" />
              {step === 1 ? `Vider ${label} ?` : `Confirmer la suppression ?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#a1a1aa]">
              {step === 1 ? (
                <>
                  Cette action va <b className="text-[#f87171]">supprimer définitivement</b> {label}.
                  <br />Cela ne peut <b>pas</b> être annulé.
                </>
              ) : (
                <>Êtes-vous <b className="text-[#f87171]">absolument certain</b> ? Dernière confirmation.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-[#27272a] text-white hover:bg-[#1a1a21]"
              data-testid={`${testId}-cancel`}
              onClick={close}
            >
              Annuler
            </AlertDialogCancel>
            {step === 1 ? (
              <AlertDialogAction
                className="bg-[#f87171] hover:bg-[#ef4444] text-white"
                data-testid={`${testId}-next`}
                onClick={(e) => { e.preventDefault(); setStep(2); }}
              >
                Continuer
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                disabled={busy}
                className="bg-[#dc2626] hover:bg-[#991b1b] text-white"
                data-testid={`${testId}-confirm`}
                onClick={wipe}
              >
                {busy ? "Suppression..." : "Oui, tout vider"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
